// Mock fixtures consumed by JOBE-0157 `flaggedFetch`.
// - W4 (JOBE-0163): `wccDepartmentDetail` — Finance dept across 4 framings.
// - W2 (JOBE-0161.2): `agentDetailQuality` — single-agent quality strip.
// - W2 (JOBE-0161.3): `agentDetailOutcomes` — recent task outcomes table.

import { FIXTURES } from './lib/flags.js';

export const wccDepartmentDetail = {
  finance: {
    slug: 'finance',
    displayName: 'Finance',
    memberCount: { humans: 4, agents: 3 },

    goals: [
      {
        title: 'Reduce AP cycle time',
        owner: 'Priya Rao',
        dueDate: '2026-06-30',
        status: 'watch',
        initiatives: [
          { title: 'Auto-classify vendor IBANs', status: 'in-progress' },
          { title: 'Migrate AP queue to Sara v2', status: 'todo' },
        ],
      },
      {
        title: 'Close FY26 Q2 books on schedule',
        owner: 'Priya Rao',
        dueDate: '2026-07-15',
        status: 'ok',
        initiatives: [
          { title: 'Reconcile Plaid transactions', status: 'in-progress' },
          { title: 'Draft management report', status: 'todo' },
        ],
      },
      {
        title: 'Reduce vendor invoice dispute rate',
        owner: 'Kenji Tan',
        dueDate: '2026-09-30',
        status: 'ok',
        initiatives: [
          { title: 'Root-cause IBAN misclassification spikes', status: 'in-progress' },
        ],
      },
      {
        title: 'Implement automated GL variance alerts',
        owner: 'Kenji Tan',
        dueDate: '2026-12-31',
        status: 'todo',
        initiatives: [],
      },
    ],

    okrs: [
      {
        objective: 'Halve AP cycle time by EOQ',
        keyResults: [
          { title: 'AP cycle time p50 < 4d', current: 5.2, target: 4, unit: 'd', tone: 'bad', spark: [6.1, 5.8, 5.5, 5.4, 5.3, 5.2, 5.2] },
          { title: 'Invoice auto-classification rate > 85%', current: 71, target: 85, unit: '%', tone: 'warn', spark: [60, 62, 65, 68, 69, 71, 71] },
          { title: 'AP queue SLA breach rate < 2%', current: 4.1, target: 2, unit: '%', tone: 'bad', spark: [6.0, 5.5, 5.1, 4.8, 4.5, 4.2, 4.1] },
        ],
      },
      {
        objective: 'Achieve clean Q2 close',
        keyResults: [
          { title: 'GL reconciliation open items < 5', current: 8, target: 5, unit: '', tone: 'warn', spark: [18, 15, 12, 11, 9, 8, 8] },
          { title: 'Management report delivered on time', current: 0, target: 1, unit: '', tone: 'neutral', spark: [0, 0, 0, 0, 0, 0, 0] },
        ],
      },
    ],

    kpis: [
      { id: 'ap-cycle-p50',    label: 'AP cycle p50',       value: 5.2,  unit: 'd',  tone: 'bad',  spark: [6.1, 5.8, 5.5, 5.4, 5.3, 5.2, 5.2], target: 4 },
      { id: 'invoice-auto',    label: 'Invoice auto-class', value: 71,   unit: '%',  tone: 'warn', spark: [60, 62, 65, 68, 69, 71, 71],          target: 85 },
      { id: 'sla-breach',      label: 'SLA breach rate',    value: 4.1,  unit: '%',  tone: 'bad',  spark: [6.0, 5.5, 5.1, 4.8, 4.5, 4.2, 4.1],  target: 2 },
      { id: 'gl-open',         label: 'GL open items',      value: 8,    unit: '',   tone: 'warn', spark: [18, 15, 12, 11, 9, 8, 8],             target: 5 },
      { id: 'agents-active',   label: 'Agents active',      value: 3,    unit: '',   tone: 'ok',   spark: [3, 3, 3, 3, 3, 3, 3],                 target: 3 },
      { id: 'eval-pass',       label: 'Eval pass rate',     value: 0.87, unit: '%',  tone: 'warn', spark: [0.94, 0.91, 0.90, 0.89, 0.88, 0.87, 0.87], target: 0.95 },
      { id: 'task-throughput', label: 'Tasks / week',       value: 47,   unit: '',   tone: 'ok',   spark: [38, 41, 43, 45, 46, 47, 47],           target: null },
      { id: 'cost-per-task',   label: 'Cost / task',        value: 0.34, unit: '$',  tone: 'ok',   spark: [0.41, 0.39, 0.37, 0.36, 0.35, 0.34, 0.34], target: 0.40 },
    ],

    scorecard: {
      categories: [
        {
          name: 'People',
          metrics: [
            { name: 'Headcount (human)',   current: 4,    target: 4,    unit: '',  variance: 0,     status: 'ok' },
            { name: 'Agents active',       current: 3,    target: 3,    unit: '',  variance: 0,     status: 'ok' },
            { name: 'Eval pass rate',      current: 0.87, target: 0.95, unit: '%', variance: -0.08, status: 'warn' },
          ],
        },
        {
          name: 'Financials',
          metrics: [
            { name: 'Cost / task',         current: 0.34, target: 0.40, unit: '$', variance: +0.06, status: 'ok' },
            { name: 'Monthly agent spend', current: 1240, target: 1500, unit: '$', variance: +260,  status: 'ok' },
          ],
        },
        {
          name: 'Operations',
          metrics: [
            { name: 'AP cycle p50',        current: 5.2,  target: 4,    unit: 'd', variance: -1.2,  status: 'bad' },
            { name: 'SLA breach rate',     current: 4.1,  target: 2,    unit: '%', variance: -2.1,  status: 'bad' },
            { name: 'Invoice auto-class',  current: 71,   target: 85,   unit: '%', variance: -14,   status: 'warn' },
            { name: 'Tasks / week',        current: 47,   target: null, unit: '',  variance: null,  status: 'ok' },
          ],
        },
        {
          name: 'Risk',
          metrics: [
            { name: 'GL open items',       current: 8,    target: 5,    unit: '',  variance: -3,    status: 'warn' },
            { name: 'SLA breach trend',    current: 4.1,  target: 2,    unit: '%', variance: -2.1,  status: 'bad' },
          ],
        },
      ],
    },
  },
};

FIXTURES.set('wcc.mock.departments', () => wccDepartmentDetail);

// ---- JOBE-0161.2: W2 Agent Detail quality strip ----
// Single-agent quality KPI fixture for W2 quality strip.
// Keyed as 'wcc.mock.roster' in flags.js FIXTURES (see followup: split into
// dedicated 'wcc.mock.agentDetail' flag to avoid sharing semantics with the
// CC roster fixture).
export const agentDetailQuality = {
  evalPassRate:   { value: 0.82, history: [0.91, 0.88, 0.86, 0.85, 0.82], target: 0.90 },
  taskThroughput: { value: 17,   history: [21,   19,   20,   18,   17],   target: null  },
  p50LatencyMs:   { value: 1240, history: [850,  920,  1020, 1140, 1240],  target: 1000 },
  errorRate:      { value: 0.08, history: [0.03, 0.04, 0.05, 0.06, 0.08], target: 0.05 },
};

FIXTURES.set('wcc.mock.roster', () => agentDetailQuality);
// JOBE-0161.2 followup: dedicated key for AgentDetail quality strip, separate from W1 roster.
FIXTURES.set('wcc.mock.agentDetail', () => agentDetailQuality);

// ---- JOBE-0161.3: W2 Agent Detail outcomes table ----
// 6 recent task rows. Two consecutive 'bad' rows reflect the J3 scenario
// (provider 503 pattern).
export const agentDetailOutcomes = [
  { id: 'task-001', title: 'AP vendor invoice reconciliation',
    status: 'ok',      startedAt: '2026-05-23T08:00Z', durationMs: 14200 },
  { id: 'task-002', title: 'Accounts payable cycle summary',
    status: 'bad',     startedAt: '2026-05-23T07:15Z', durationMs: 3100 },
  { id: 'task-003', title: 'Wire transfer validation run',
    status: 'bad',     startedAt: '2026-05-23T06:50Z', durationMs: 2800 },
  { id: 'task-004', title: 'Daily spend categorization',
    status: 'ok',      startedAt: '2026-05-22T22:00Z', durationMs: 28500 },
  { id: 'task-005', title: 'Forecast model refresh',
    status: 'warn',    startedAt: '2026-05-22T18:30Z', durationMs: 91000 },
  { id: 'task-006', title: 'Monthly close pre-check',
    status: 'pending', startedAt: '2026-05-23T09:00Z', durationMs: null  },
];

FIXTURES.set('agent-detail-outcomes-mock', () => agentDetailOutcomes);

// ---- JOBE-0162.1: W3 Organization universal directory ----
// Fused humans + agents directory consumed by /organization (W3).
// Pinned row shape (umbrella JOBE-0162 §"Unified row shape"):
//   { type, id, displayName, role, dept, ownerId, phase, ...type-specific }
//
// Canonical 5 departments: research / finance / platform / executive / legal.
// 7 agents + 18 humans = 25 rows. Every agent's ownerId points at a human
// in this fixture (no dangling owners). At least one human (Priya Rao) owns
// multiple agents per the J5 storyboard.
export const wccOrganization = [
  // ---- agents (7) ----
  { type: 'agent', id: 'sara', displayName: 'Sara',
    role: 'Cash Forecast Analyst', dept: 'finance',
    ownerId: 'priya-rao', phase: 'Available',
    evalPassRate: 0.94, promptVersion: 17 },
  { type: 'agent', id: 'iris', displayName: 'Iris',
    role: 'Invoice Reconciler', dept: 'finance',
    ownerId: 'priya-rao', phase: 'Degraded',
    evalPassRate: 0.78, promptVersion: 9 },
  { type: 'agent', id: 'janus', displayName: 'Janus',
    role: 'Spend Categorizer', dept: 'finance',
    ownerId: 'priya-rao', phase: 'Available',
    evalPassRate: 0.91, promptVersion: 12 },
  { type: 'agent', id: 'priya', displayName: 'Priya',
    role: 'Research Synthesizer', dept: 'research',
    ownerId: 'helen-osei', phase: 'Available',
    evalPassRate: 0.88, promptVersion: 22 },
  { type: 'agent', id: 'archivist', displayName: 'Archivist',
    role: 'Document Curator', dept: 'research',
    ownerId: 'helen-osei', phase: 'Available',
    evalPassRate: 0.96, promptVersion: 31 },
  { type: 'agent', id: 'dmitri', displayName: 'Dmitri',
    role: 'Board-Brief Composer', dept: 'executive',
    ownerId: 'shawn-lower', phase: 'Available',
    evalPassRate: 0.85, promptVersion: 14 },
  { type: 'agent', id: 'marcus', displayName: 'Marcus',
    role: 'Vendor Contract Reviewer', dept: 'legal',
    ownerId: 'naomi-fitch', phase: 'Disabled',
    evalPassRate: 0.62, promptVersion: 5 },

  // ---- humans (18) ----
  // executive (3)
  { type: 'human', id: 'shawn-lower', displayName: 'Shawn Lower',
    role: 'Founder', dept: 'executive',
    ownerId: null, phase: null,
    email: 'shawn@example.com', slack: 'shawn' },
  { type: 'human', id: 'amelia-vargas', displayName: 'Amelia Vargas',
    role: 'Chief of Staff', dept: 'executive',
    ownerId: 'shawn-lower', phase: null,
    email: 'amelia@example.com', slack: 'amelia' },
  { type: 'human', id: 'rohan-mehta', displayName: 'Rohan Mehta',
    role: 'Executive Assistant', dept: 'executive',
    ownerId: 'amelia-vargas', phase: null,
    email: 'rohan@example.com', slack: 'rohan' },

  // finance (4) — Priya owns the finance agent cluster (J5)
  { type: 'human', id: 'priya-rao', displayName: 'Priya Rao',
    role: 'Quality Lead', dept: 'finance',
    ownerId: 'kenji-tan', phase: null,
    email: 'priya@example.com', slack: 'priya' },
  { type: 'human', id: 'kenji-tan', displayName: 'Kenji Tan',
    role: 'Head of Finance', dept: 'finance',
    ownerId: 'shawn-lower', phase: null,
    email: 'kenji@example.com', slack: 'kenji' },
  { type: 'human', id: 'aisha-okafor', displayName: 'Aisha Okafor',
    role: 'Senior Accountant', dept: 'finance',
    ownerId: 'kenji-tan', phase: null,
    email: 'aisha@example.com', slack: 'aisha' },
  { type: 'human', id: 'paolo-rossi', displayName: 'Paolo Rossi',
    role: 'AP Analyst', dept: 'finance',
    ownerId: 'priya-rao', phase: null,
    email: 'paolo@example.com', slack: 'paolo' },

  // research (4)
  { type: 'human', id: 'helen-osei', displayName: 'Helen Osei',
    role: 'Head of Research', dept: 'research',
    ownerId: 'shawn-lower', phase: null,
    email: 'helen@example.com', slack: 'helen' },
  { type: 'human', id: 'lin-fang', displayName: 'Lin Fang',
    role: 'Research Engineer', dept: 'research',
    ownerId: 'helen-osei', phase: null,
    email: 'lin@example.com', slack: 'lin' },
  { type: 'human', id: 'olivia-bennett', displayName: 'Olivia Bennett',
    role: 'Knowledge Curator', dept: 'research',
    ownerId: 'helen-osei', phase: null,
    email: 'olivia@example.com', slack: 'olivia' },
  { type: 'human', id: 'tariq-haddad', displayName: 'Tariq Haddad',
    role: 'Data Librarian', dept: 'research',
    ownerId: 'helen-osei', phase: null,
    email: 'tariq@example.com', slack: 'tariq' },

  // platform (4)
  { type: 'human', id: 'morgan-blake', displayName: 'Morgan Blake',
    role: 'Head of Platform', dept: 'platform',
    ownerId: 'shawn-lower', phase: null,
    email: 'morgan@example.com', slack: 'morgan' },
  { type: 'human', id: 'eduardo-silva', displayName: 'Eduardo Silva',
    role: 'Site Reliability Engineer', dept: 'platform',
    ownerId: 'morgan-blake', phase: null,
    email: 'eduardo@example.com', slack: 'eduardo' },
  { type: 'human', id: 'yuki-watanabe', displayName: 'Yuki Watanabe',
    role: 'Platform Engineer', dept: 'platform',
    ownerId: 'morgan-blake', phase: null,
    email: 'yuki@example.com', slack: 'yuki' },
  { type: 'human', id: 'isabel-cruz', displayName: 'Isabel Cruz',
    role: 'Developer Experience', dept: 'platform',
    ownerId: 'morgan-blake', phase: null,
    email: 'isabel@example.com', slack: 'isabel' },

  // legal (3)
  { type: 'human', id: 'naomi-fitch', displayName: 'Naomi Fitch',
    role: 'General Counsel', dept: 'legal',
    ownerId: 'shawn-lower', phase: null,
    email: 'naomi@example.com', slack: 'naomi' },
  { type: 'human', id: 'gregor-novak', displayName: 'Gregor Novak',
    role: 'Contracts Paralegal', dept: 'legal',
    ownerId: 'naomi-fitch', phase: null,
    email: 'gregor@example.com', slack: 'gregor' },
  { type: 'human', id: 'fatima-khan', displayName: 'Fatima Khan',
    role: 'Compliance Analyst', dept: 'legal',
    ownerId: 'naomi-fitch', phase: null,
    email: 'fatima@example.com', slack: 'fatima' },
];

FIXTURES.set('wcc.mock.organization', () => wccOrganization);

// JOBE-0162.5: human profile recent-activity mock feed.
// Keyed by person id; only the people who appear here render an activity
// section. Per spec, fixture covers Priya + one other human (Helen) so
// the empty-state path is exercised by the other 16 humans.
export const wccOrganizationActivity = [
  {
    personId: 'priya-rao',
    events: [
      { ts: '2026-05-22T14:31Z', kind: 'correction',
        text: 'Approved currency-parsing correction on Sara@17',
        refUrl: '/agent/sara' },
      { ts: '2026-05-21T09:15Z', kind: 'review',
        text: "Reviewed Iris's weekly eval rollup",
        refUrl: '/agent/iris' },
      { ts: '2026-05-20T16:02Z', kind: 'comment',
        text: 'Commented on Janus spend-categorizer drift report',
        refUrl: '/agent/janus' },
    ],
  },
  {
    personId: 'helen-osei',
    events: [
      { ts: '2026-05-22T11:00Z', kind: 'review',
        text: 'Reviewed Archivist prompt update',
        refUrl: '/agent/archivist' },
      { ts: '2026-05-19T08:42Z', kind: 'correction',
        text: 'Corrected Priya synthesis on Q1 board materials',
        refUrl: '/agent/priya' },
    ],
  },
];

FIXTURES.set('wcc.mock.organization.activity', () => wccOrganizationActivity);

// ---- JOBE-0165.1/.2/.3/.4: W5 PromptOps Agent fixture ----
// Single-agent (priya@17) fixture for the W5 PromptOps Agent workspace.
// Intentionally single-agent for demo: every route under
// /promptops/agent/:name renders priya's data when the mock flag is on.
// The displayName "Priya" makes the mock state visually obvious regardless
// of the URL :name param.
//
// JOBE-0165.1: identity-head + quality-strip fields.
// JOBE-0165.2: prompt-version stack fields (version, candidateVersion, etc.).
// JOBE-0165.3: failureClusters array (3 clusters for ?failureClass= scroll test).
// JOBE-0165.4: evalRuns array (5 entries showing recovery).
export const promptOpsAgentDetail = {
  name:        'priya',
  displayName: 'Priya',
  role:        'urn:jobe:agent-role:finance-analyst',
  dept:        'urn:jobe:department:finance',
  owner:       'urn:jobe:person:shawn',
  phase:       'Running',

  // Quality strip (JOBE-0165.1) — 5 KPI tiles, absolute-mode sparks with target lines.
  evalPassRate:        0.94,
  evalPassRateHistory: [0.97, 0.96, 0.95, 0.95, 0.94],
  taskCount:           23,
  taskCountHistory:    [18, 21, 22, 24, 23],
  p50LatencyMs:        480,
  p50LatencyMsHistory: [410, 430, 450, 470, 480],
  errorRate:           0.053,
  errorRateHistory:    [0.04, 0.04, 0.05, 0.05, 0.053],
  tokenCost:           0.42,
  tokenCostHistory:    [0.31, 0.34, 0.37, 0.39, 0.42],

  // Prompt-version stack (JOBE-0165.2) — active + candidate version metadata.
  // Identifier rendered as `${name}@${version}` (lowercase per D12).
  version:                17,
  candidateVersion:       18,
  activeCommit:           'a3f1e92',
  activeAuthor:           'shawn',
  activeDiffSummary:      'baseline (no parent)',
  activeCompileStatus:    'green',
  activeEvalStatus:       'pass · 94%',
  candidateCommit:        'b6c4d18',
  candidateAuthor:        'shawn',
  candidateDiffSummary:   '+ currency normaliser; 3 system-prompt lines changed',
  candidateCompileStatus: 'pending',
  candidateEvalStatus:    'not-run',

  // Failure clusters (JOBE-0165.3) — three clusters; the ?failureClass=
  // scroll-into-view test uses "date-range-boundary" as its scroll target.
  // Also consumed by JOBE-0165.4's fix-bundle card.
  failureClusters: [
    {
      cls: 'currency / non-USD parsing',
      count: 4,
      evidence: [
        { traceId: 'tr-001', input: '€1.200,00', expected: '1200.00', actual: '1.2' },
        { traceId: 'tr-002', input: '¥12000',    expected: '12000',   actual: '12'  },
      ],
      proposedFix: 'Pre-normalise currency strings to USD decimal before arithmetic.',
    },
    {
      cls: 'date-range boundary',
      count: 2,
      evidence: [
        { traceId: 'tr-007', input: '2026-01-01..2026-01-31', expected: '31 days', actual: '30 days' },
      ],
      proposedFix: 'Use inclusive end-date semantics in DateRange.days().',
    },
    {
      cls: 'IBAN length validation',
      count: 1,
      evidence: [
        { traceId: 'tr-013', input: 'DE89370400440532013000XYZ', expected: 'invalid', actual: 'accepted' },
      ],
      proposedFix: 'Tighten IBAN validator: ≤ 34 chars per ISO 13616.',
    },
  ],

  // Eval runs (JOBE-0165.4) — 5 entries showing a recent recovery (94% after
  // a 91% trough), matching the J1 narrative of an operator watching the
  // eval recover after a shipped correction.
  evalRuns: [
    { runId: 'er-040', ts: '2026-05-19T10:00Z', passRate: 0.97, suiteId: 'finance-core' },
    { runId: 'er-041', ts: '2026-05-20T10:00Z', passRate: 0.95, suiteId: 'finance-core' },
    { runId: 'er-042', ts: '2026-05-21T10:00Z', passRate: 0.92, suiteId: 'finance-core' },
    { runId: 'er-043', ts: '2026-05-22T10:00Z', passRate: 0.91, suiteId: 'finance-core' },
    { runId: 'er-044', ts: '2026-05-23T10:00Z', passRate: 0.94, suiteId: 'finance-core' },
  ],
};

FIXTURES.set('promptops.mock.agentDetail', () => promptOpsAgentDetail);
// ---- JOBE-0160.1: W1 KPI strip ----
export const wccKpi = {
  agentsActive:       12,
  agentsDegraded:      1,
  agentsDisabled:      0,
  proposalsOpen:       5,
  approvalsBlocked:    2,
  tasksInFlight:      34,
};
FIXTURES.set('wcc.mock.kpi', () => wccKpi);

// ---- JOBE-0160.2: W1 attention list ----
export const wccAttention = [
  {
    id: 'attn-001',
    kind: 'decide',
    urgency: 'high',
    agentName: 'priya',
    dept: 'Finance',
    title: 'AP cycle time SLA breach — escalate or hold?',
    meta: ['DUE 14:00'],
  },
  {
    id: 'attn-002',
    kind: 'approve',
    urgency: 'medium',
    agentName: 'sara',
    dept: 'Finance',
    title: 'Vendor invoice batch — 14 items awaiting approval',
    meta: ['CREATED 08:12'],
  },
  {
    id: 'attn-003',
    kind: 'signal',
    urgency: 'high',
    agentName: 'iris',
    dept: 'Platform',
    text: 'Plaid provider 503 rate 18% — SLA breach imminent',
    meta: ['SINCE 07:45'],
  },
  {
    id: 'attn-004',
    kind: 'reply',
    urgency: 'medium',
    agentName: 'kenji',
    dept: 'Finance',
    title: 'GL reconciliation clarification needed from Kenji',
    meta: ['WAITING 2h'],
  },
  {
    id: 'attn-005',
    kind: 'signal',
    urgency: 'low',
    dept: 'Research',
    text: 'Audit backlog growing — 23 items, up from 18 yesterday',
    meta: [],
  },
];
FIXTURES.set('wcc.mock.attention', () => wccAttention);

// ---- JOBE-0160.3: W1 risk heat map ----
// 5 departments × 3 axes. Each cell: { dept, axis, score, agentCount }.
export const wccHeatmap = {
  cells: [
    { dept: 'Finance',   axis: 'accuracy',   score: 0.42, agentCount: 3 },
    { dept: 'Finance',   axis: 'latency',    score: 0.28, agentCount: 3 },
    { dept: 'Finance',   axis: 'compliance', score: 0.61, agentCount: 3 },
    { dept: 'Legal',     axis: 'accuracy',   score: 0.82, agentCount: 2 },
    { dept: 'Legal',     axis: 'latency',    score: 0.35, agentCount: 2 },
    { dept: 'Legal',     axis: 'compliance', score: 0.19, agentCount: 2 },
    { dept: 'Platform',  axis: 'accuracy',   score: 0.55, agentCount: 4 },
    { dept: 'Platform',  axis: 'latency',    score: 0.48, agentCount: 4 },
    { dept: 'Platform',  axis: 'compliance', score: 0.33, agentCount: 4 },
    { dept: 'Research',  axis: 'accuracy',   score: 0.17, agentCount: 2 },
    { dept: 'Research',  axis: 'latency',    score: 0.22, agentCount: 2 },
    { dept: 'Research',  axis: 'compliance', score: 0.44, agentCount: 2 },
    { dept: 'Home Ops',  axis: 'accuracy',   score: 0.31, agentCount: 1 },
    { dept: 'Home Ops',  axis: 'latency',    score: 0.15, agentCount: 1 },
    { dept: 'Home Ops',  axis: 'compliance', score: 0.27, agentCount: 1 },
  ],
};
FIXTURES.set('wcc.mock.heatmap', () => wccHeatmap);

// ---- JOBE-0160.4: W1 trend panel ----
export const wccTrend = [
  {
    id: 'eval-pass-rate',
    label: 'Eval pass rate',
    unit: '%',
    points: [0.91, 0.89, 0.88, 0.87, 0.90, 0.88, 0.86, 0.87, 0.89, 0.91, 0.90, 0.89, 0.88, 0.89],
    mean: 0.89,
  },
  {
    id: 'task-throughput',
    label: 'Task throughput',
    unit: 'tasks/day',
    points: [38, 41, 39, 43, 45, 44, 42, 46, 48, 47, 44, 46, 49, 51],
    mean: 44.5,
  },
  {
    id: 'latency-p50',
    label: 'Latency p50',
    unit: 'ms',
    points: [820, 870, 850, 910, 890, 920, 880, 860, 840, 870, 900, 920, 950, 940],
    mean: 887.8,
  },
];
FIXTURES.set('wcc.mock.trend', () => wccTrend);

// ---- JOBE-0160.5: W1 roster table (7 agents) ----
// Note: re-registers 'wcc.mock.roster' with the W1 CommandCenter roster shape.
// The JOBE-0161.2 registration above (agentDetailQuality) is overwritten here
// because the W1 roster has a different shape. W2 AgentDetail should use a
// dedicated fixture key when it lands (JOBE-0161.2 followup: split keys).
export const wccRoster = [
  { name: 'priya',   displayName: 'Priya',   role: 'Finance Agent',   dept: 'Finance',  phase: 'Available', evalPassRate: 0.94, tasksThisWeek: 47, p50LatencyMs: 420, lastActiveAgo: '3m'  },
  { name: 'sara',    displayName: 'Sara',    role: 'Finance Agent',   dept: 'Finance',  phase: 'Available', evalPassRate: 0.88, tasksThisWeek: 31, p50LatencyMs: 510, lastActiveAgo: '12m' },
  { name: 'kenji',   displayName: 'Kenji',   role: 'Finance Agent',   dept: 'Finance',  phase: 'Degraded',  evalPassRate: 0.72, tasksThisWeek: 18, p50LatencyMs: 1240, lastActiveAgo: '2h' },
  { name: 'iris',    displayName: 'Iris',    role: 'Platform Agent',  dept: 'Platform', phase: 'Available', evalPassRate: 0.80, tasksThisWeek: 24, p50LatencyMs: 480, lastActiveAgo: '8m'  },
  { name: 'morgan',  displayName: 'Morgan',  role: 'Legal Agent',     dept: 'Legal',    phase: 'Available', evalPassRate: 0.91, tasksThisWeek: 12, p50LatencyMs: 390, lastActiveAgo: '1m'  },
  { name: 'riley',   displayName: 'Riley',   role: 'Research Agent',  dept: 'Research', phase: 'Disabled',  evalPassRate: 0.60, tasksThisWeek:  0, p50LatencyMs: null, lastActiveAgo: '3d' },
  { name: 'alex',    displayName: 'Alex',    role: 'Home Ops Agent',  dept: 'Home Ops', phase: 'Available', evalPassRate: 0.85, tasksThisWeek: 19, p50LatencyMs: 560, lastActiveAgo: '25m' },
];
// Override the JOBE-0161.2 registration so W1 RosterTable gets the rich row shape.
FIXTURES.set('wcc.mock.roster', () => wccRoster);

// ---- JOBE-0164: PromptOps Fleet (W6) ----
// Pinned-shape fixture per JOBE-0157.4.
export const promptopsFleet = {
  agentCount: 7,
  deptCount: 5,
  evalPassRate: 0.89,
  agents: [
    { name: 'sara',      displayName: 'Sara',      dept: 'Finance',  role: 'AP analyst',
      evalPassRate: 0.78, evalDelta: -0.05, passRateBar: 0.78, failureCount: 11, promptVersion: 12 },
    { name: 'priya',     displayName: 'Priya',     dept: 'Research', role: 'Researcher',
      evalPassRate: 0.94, evalDelta: +0.02, passRateBar: 0.94, failureCount: 4,  promptVersion: 17 },
    { name: 'archivist', displayName: 'Archivist', dept: 'Library',  role: 'Indexer',
      evalPassRate: 0.91, evalDelta: 0.0,  passRateBar: 0.91, failureCount: 5,  promptVersion: 9  },
    { name: 'dmitri',    displayName: 'Dmitri',    dept: 'Finance',  role: 'Forecaster',
      evalPassRate: 0.86, evalDelta: -0.02, passRateBar: 0.86, failureCount: 7,  promptVersion: 22 },
    { name: 'kenji',     displayName: 'Kenji',     dept: 'Finance',  role: 'Reconciler',
      evalPassRate: 0.92, evalDelta: +0.01, passRateBar: 0.92, failureCount: 3,  promptVersion: 14 },
    { name: 'marcus',    displayName: 'Marcus',    dept: 'Ops',      role: 'Dispatcher',
      evalPassRate: 0.67, evalDelta: -0.08, passRateBar: 0.67, failureCount: 14, promptVersion: 6  },
    { name: 'rhea',      displayName: 'Rhea',      dept: 'Legal',    role: 'Reviewer',
      evalPassRate: 0.96, evalDelta: +0.03, passRateBar: 0.96, failureCount: 2,  promptVersion: 19 },
  ],
  failureClasses: [
    { cls: 'currency / non-USD parsing', agentsAffected: ['sara', 'marcus'], count: 14,
      description: 'Locale-format currency strings drop the decimal — €1.200,00 reads as 1.2.' },
    { cls: 'date-range boundary',        agentsAffected: ['priya'],          count: 7,
      description: 'Inclusive vs exclusive end-of-range mishandled around month boundaries.' },
    { cls: 'citation missing',           agentsAffected: ['archivist'],      count: 5,
      description: 'Synthesis includes claims unbacked by retrieved evidence.' },
  ],
  recentCorrections: [
    { agentName: 'sara',      version: 12, cls: 'currency / non-USD parsing', ts: '2026-05-22T14:31Z',
      note: 'Operator flagged €1.200,00 → 1.2 misread; cohort patch queued.',
      by: 'human flag', ref: 'trace #1f4a' },
    { agentName: 'archivist', version: 9,  cls: 'citation missing',           ts: '2026-05-21T09:15Z',
      note: 'Synthesis included claim without retrieval cite; retrained prompt.',
      by: 'auto-detect', ref: 'trace #2c91' },
  ],
};

FIXTURES.set('promptops.mock.fleet', () => promptopsFleet);

// ---- JOBE-0167.5: W8 Forget log feed ----
export const memoryMockForgetting = {
  events: [
    {
      id: 'fl-0001',
      ts: '2026-05-22T14:31:00Z',
      operator: 'shawn',
      reason_class: 'privacy',
      reason: 'GDPR erasure',
      urn: 'urn:jobe:memory:m-0005',
      body_snippet: 'Shawn reviewed Q1 invoice batch on 2026-03-15',
      lifecycle: 'tombstoned',
      cascade: { summary_count: 1, derived_from_count: 3, retrieval_cache_count: 7 },
    },
    {
      id: 'fl-0002',
      ts: '2026-05-21T09:15:00Z',
      operator: 'priya',
      reason_class: 'compliance',
      reason: 'PCI scope contraction',
      urn: 'urn:jobe:memory:m-0099',
      body_snippet: 'Card-number fragment detected in extracted note',
      lifecycle: 'hard_deleted',
      cascade: { summary_count: 0, derived_from_count: 1, retrieval_cache_count: 2 },
    },
    {
      id: 'fl-0003',
      ts: '2026-05-20T16:42:00Z',
      operator: 'kenji',
      reason_class: 'user-request',
      reason: 'Subject requested removal',
      urn: 'urn:jobe:memory:m-0117',
      body_snippet: 'Vendor X said to remove their incident note',
      lifecycle: 'tombstoned',
      cascade: { summary_count: 2, derived_from_count: 4, retrieval_cache_count: 5 },
    },
  ],
  nextCursor: null,
};

FIXTURES.set('memory.mock.forgetting', () => memoryMockForgetting);

// ---- JOBE-0167.5: W8 Decay tab — memories near the decay threshold ----
// Shares the memory.mock.conflicts fixture key per the spec (the fixture
// carries both conflict and decay metadata so both surfaces can share it).
// Five rows spanning decay ETAs of 1, 3, 7, 14, 21 days.
function decayCurve(start, end, n = 30) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    out.push(start + (end - start) * t);
  }
  return out;
}

export const memoryMockConflicts = {
  memories: [
    {
      urn: 'urn:jobe:memory:c-0001',
      body_snippet: 'Shawn flagged Monday briefing as too long — trim to 3 items.',
      decay: { score: 0.32, threshold: 0.30, eta_days: 1, curve: decayCurve(0.92, 0.30) },
    },
    {
      urn: 'urn:jobe:memory:c-0002',
      body_snippet: 'AP-classifier outputs IBAN region tag; cite the regulator before posting.',
      decay: { score: 0.42, threshold: 0.30, eta_days: 3, curve: decayCurve(0.88, 0.34) },
    },
    {
      urn: 'urn:jobe:memory:c-0003',
      body_snippet: 'Janus rollout uses canary 5%/25%/100% with 30-minute soak windows.',
      decay: { score: 0.55, threshold: 0.30, eta_days: 7, curve: decayCurve(0.92, 0.42) },
    },
    {
      urn: 'urn:jobe:memory:c-0004',
      body_snippet: 'Hypothesis: Janus latency p99 correlates with episodic-memory pressure.',
      decay: { score: 0.62, threshold: 0.30, eta_days: 14, curve: decayCurve(0.92, 0.50) },
    },
    {
      urn: 'urn:jobe:memory:c-0005',
      body_snippet: 'Last week: 3 alerts, 2 acknowledged within SLO; one paged on-call.',
      decay: { score: 0.74, threshold: 0.30, eta_days: 21, curve: decayCurve(0.92, 0.58) },
    },
  ],
};

FIXTURES.set('memory.mock.conflicts', () => memoryMockConflicts);

// ---- JOBE-0167.2: W8 Memories tab — 8 rows across the 4 lifecycle states ----
// First row is the canonical "Shawn / morning briefings preference" example.
// Wire field name is `kind` per JOBE-0076.1; the UI normalizes kind→type.
export const memoryMockList = {
  memories: [
    {
      urn: 'urn:jobe:memory:m-0001',
      subject: 'urn:jobe:person:shawn',
      owner: 'urn:jobe:agent:research:archivist',
      kind: 'preference',
      tier: 'semantic',
      lifecycle: 'active',
      body: 'Shawn prefers morning briefings before 8am, with the lead headline first.',
      confidence: 0.86,
      valid_from: '2026-05-01T00:00:00Z',
      valid_to: null,
      asserted_at: '2026-05-01T07:13:00Z',
    },
    {
      urn: 'urn:jobe:memory:m-0002',
      subject: 'urn:jobe:person:shawn',
      owner: 'urn:jobe:agent:research:archivist',
      kind: 'preference',
      tier: 'semantic',
      lifecycle: 'superseded',
      body: 'Shawn prefers afternoon briefings (superseded 2026-05-01).',
      confidence: 0.64,
      valid_from: '2026-04-15T00:00:00Z',
      valid_to: '2026-05-01T00:00:00Z',
      asserted_at: '2026-04-15T09:01:00Z',
    },
    {
      urn: 'urn:jobe:memory:m-0003',
      subject: 'urn:jobe:project:janus',
      owner: 'urn:jobe:agent:platform:ops',
      kind: 'decision',
      tier: 'episodic',
      lifecycle: 'active',
      body: 'Janus rollout uses canary 5%/25%/100% with 30-minute soak windows.',
      confidence: 0.92,
      valid_from: '2026-05-10T00:00:00Z',
      valid_to: null,
      asserted_at: '2026-05-10T14:22:00Z',
    },
    {
      urn: 'urn:jobe:memory:m-0004',
      subject: 'urn:jobe:agent:finance:priya',
      owner: 'urn:jobe:agent:research:archivist',
      kind: 'procedure-lesson',
      tier: 'procedural',
      lifecycle: 'active',
      body: 'AP-classifier outputs IBAN region tag; cite the regulator before posting.',
      confidence: 0.78,
      valid_from: '2026-04-20T00:00:00Z',
      valid_to: null,
      asserted_at: '2026-04-20T11:00:00Z',
    },
    {
      urn: 'urn:jobe:memory:m-0005',
      subject: 'urn:jobe:person:shawn',
      owner: 'urn:jobe:agent:research:archivist',
      kind: 'observation',
      tier: 'episodic',
      lifecycle: 'tombstoned',
      body: 'Shawn reviewed Q1 invoice batch on 2026-03-15 (tombstoned, GDPR).',
      confidence: 0.55,
      valid_from: '2026-03-15T00:00:00Z',
      valid_to: '2026-05-12T00:00:00Z',
      asserted_at: '2026-03-15T16:42:00Z',
    },
    {
      urn: 'urn:jobe:memory:m-0006',
      subject: 'urn:jobe:topic:weekly-briefing',
      owner: 'urn:jobe:agent:research:archivist',
      kind: 'summary',
      tier: 'semantic',
      lifecycle: 'active',
      body: 'Last week: 3 alerts, 2 acknowledged within SLO; one paged on-call.',
      confidence: 0.81,
      valid_from: '2026-05-18T00:00:00Z',
      valid_to: null,
      asserted_at: '2026-05-18T07:00:00Z',
    },
    {
      urn: 'urn:jobe:memory:m-0007',
      subject: 'urn:jobe:project:janus',
      owner: 'urn:jobe:agent:research:archivist',
      kind: 'belief',
      tier: 'semantic',
      lifecycle: 'candidate-implicit',
      body: 'Hypothesis: Janus latency p99 correlates with episodic-memory pressure.',
      confidence: 0.42,
      valid_from: '2026-05-19T00:00:00Z',
      valid_to: null,
      asserted_at: '2026-05-19T13:30:00Z',
    },
    {
      urn: 'urn:jobe:memory:m-0008',
      subject: 'urn:jobe:person:shawn',
      owner: 'urn:jobe:agent:research:archivist',
      kind: 'feedback',
      tier: 'working',
      lifecycle: 'candidate-implicit',
      body: 'Shawn flagged Monday briefing as too long — trim to 3 items.',
      confidence: 0.48,
      valid_from: '2026-05-21T00:00:00Z',
      valid_to: null,
      asserted_at: '2026-05-21T08:14:00Z',
    },
  ],
  nextCursor: null,
  totalCount: 8,
};

FIXTURES.set('memory.mock.list', () => memoryMockList);
